import React from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { BASE_URL } from '@constants/environment';
import { Button, Card, Dialog, Paragraph as P, Text, FieldHint, TextField, RichTextEditor } from '@devfolioco/genesis';
import { usePrevious, isValidURL, saveImage, Analytics } from '@devfolioco/helpers';

import Page from 'components/Page';
import Box from 'components/Box';
import Navigation from 'components/Navigation';
import RegisterWall from 'components/RegisterWall';
import ideaSrc from 'assets/idea.png';
import wrenchSrc from 'assets/wrench.png';
import useAction from 'helpers/useAction';
import useActiveField from 'helpers/useActiveField';
import { ACTION_STATUS as STATUS, ANALYTICS_TRACKING_EVENTS } from '@constants/index';
import { submitIdea } from 'actions/idea';

import { WrenchImg, IdeaImg, Container } from './style';

const title = 'Submit an idea';
const subtitle = 'Create a new account or sign in to Devfolio to continue';

const mapTitleToView = {
  SIGNIN: title,
  SIGNUP: title,
  VERIFY: "You've got mail!",
};

const mapSubtitleToView = {
  SIGNIN: subtitle,
  SIGNUP: subtitle,
  VERIFY: null,
};

const OG_INFO = {
  title: 'Submit Your Idea | Devfolio',
  description:
    'Got an idea? Let us know. We have a community of builders who can turn your ideas into real-world products.',
  image: `${BASE_URL}static/got-idea-og.jpg`,
  url: 'https://devfolio.co/idea/submit',
};

const Hint = React.memo(({ isActive, children }) => (
  <FieldHint
    active={isActive}
    width="250px"
    style={{
      top: 28,
      left: 'calc(100% + 40px)',
      zIndex: 3,
    }}
  >
    <Text upgrade size={16} color="white-0">
      {children}
    </Text>
  </FieldHint>
));

const FieldWrapper = React.memo(({ fieldType, isActive, hint, style, ...props }) => {
  const FieldType = fieldType === 'richTextEditor' ? RichTextEditor : TextField;
  return (
    <Box style={{ position: 'relative', ...style }}>
      <FieldType
        {...props}
        style={{ width: '100%' }}
        saveImage={fieldType === 'richTextEditor' ? saveImage : undefined}
      />
      {hint && <Hint isActive={isActive}>{hint}</Hint>}
    </Box>
  );
});

const EMPTY_IDEA = {
  name: '',
  idea: '',
  source: '',
};

const SubmitIdea = () => {
  const [values, setValues] = React.useState(EMPTY_IDEA);
  const [showSuccess, toggleSuccess] = React.useState(false);
  const dispatchSubmitIdea = useAction(submitIdea);
  const submitIdeaStatus = useSelector(({ idea }) => idea.status.submitIdea);
  const prevSubmitIdeaStatus = usePrevious(submitIdeaStatus);
  const { activeField, handleFocus, handleBlur } = useActiveField();

  React.useEffect(() => {
    if (submitIdeaStatus !== prevSubmitIdeaStatus && submitIdeaStatus === STATUS.SUCCESS) {
      Analytics.trackEvent(ANALYTICS_TRACKING_EVENTS.SUBMTTED_AN_IDEA);
      toggleSuccess(true);
    }
  }, [prevSubmitIdeaStatus, submitIdeaStatus]);

  const handleSubmit = e => {
    if (e) {
      e.preventDefault();
    }
    dispatchSubmitIdea(values);
  };

  const reset = React.useCallback(() => {
    setValues(EMPTY_IDEA);
    toggleSuccess(false);
  }, []);

  const handleChange = React.useCallback(({ target: { name, value } }) => {
    setValues(currentValue => ({ ...currentValue, [name]: value }));
  }, []);
  const isAuthenticated = useSelector(({ authentication }) => authentication.isAuthenticated);
  const isEmailVerified = useSelector(({ user }) => user.isEmailVerified);
  const [showRegisterWall, toggleRegisterWall] = React.useState(false);

  const isSubmitDisabled =
    !(values.idea.trim() && values.name.trim()) || (!!values.source.trim() && !isValidURL(values.source));

  const handleSubmitClick = e => {
    if (isAuthenticated && isEmailVerified) {
      handleSubmit(e);
    } else {
      e.preventDefault();
      toggleRegisterWall(true);
    }
  };
  return (
    <>
      <Helmet>
        <title>{OG_INFO.title}</title>
        <meta property="og:site_name" content="Devfolio" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={OG_INFO.url} />
        <meta property="og:title" content={OG_INFO.title} />
        <meta property="og:description" content={OG_INFO.description} />
        <meta property="og:image" content={OG_INFO.image} />
        <meta property="og:locale" content="en_US" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@devfolio" />
        <meta name="twitter:title" content={OG_INFO.title} />
        <meta name="twitter:description" content={OG_INFO.description} />
        <meta name="twitter:url" content={OG_INFO.url} />
        <meta name="twitter:image" content={OG_INFO.image} />
      </Helmet>
      {showSuccess && (
        <Dialog
          title="Idea Submitted Successfully!"
          primaryAction={{
            label: 'Okay',
            onAction: reset,
          }}
          noCancel
          cancelAction={{ label: 'Cancel', onAction: reset }}
          onClose={reset}
        >
          <P style={{ textAlign: 'center' }}>
            We have received your idea, we&apos;ll let you know once it is approved.
          </P>
        </Dialog>
      )}
      {showRegisterWall && (
        <RegisterWall
          mapTitleToView={mapTitleToView}
          mapSubtitleToView={mapSubtitleToView}
          postAuthentication={handleSubmit}
          event={ANALYTICS_TRACKING_EVENTS.REGISTER_WALL_SUBMIT_IDEA}
          onClose={() => toggleRegisterWall(false)}
          closeOnClickOutside
          closeOnEsc
          requireEmailVerification
        />
      )}

      <Navigation />
      <Page style={{ alignItems: 'center' }}>
        <Container>
          <Box style={{ textAlign: 'center', width: '100%', flexDirection: 'column' }} mb={12} mt={4}>
            <Text size={36} weight="black" color="grey-8" upgrade>
              Got an idea?
            </Text>
            <Text as="p" size={20} weight="medium" color="grey-7" upgrade>
              Ideas are nothing until executed. We have a community of builders who can turn your ideas into real-world
              products.
            </Text>
          </Box>
          <Box style={{ position: 'relative' }}>
            <WrenchImg src={wrenchSrc} alt="New ideas" />
            <IdeaImg src={ideaSrc} alt="New ideas" />
          </Box>
          <Card style={{ width: '100%', marginBottom: '64px', zIndex: 4, position: 'relative' }}>
            <form onSubmit={handleSubmitClick}>
              <FieldWrapper
                value={values.name}
                label="idea"
                maxlength={200}
                name="name"
                placeholder="E.g. An app that turns recorded traffic noises to music."
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isActive={activeField === 'name'}
                required
                autoFocus
              />
              <FieldWrapper
                fieldType="richTextEditor"
                value={values.idea}
                style={{ marginTop: '24px' }}
                label="Brief decription of the idea"
                maxlength={2000}
                name="idea"
                onChange={handleChange}
                required
                onFocus={handleFocus}
                onBlur={handleBlur}
                isActive={activeField === 'idea'}
                hint={
                  <>
                    Write a brief, sharp and on point <strong>description</strong> of your idea. A clear and detailed
                    description will help builders understanding the idea.
                  </>
                }
                instruction={`${values.idea.length}/2000 characters`}
              />
              <FieldWrapper
                value={values.source}
                label="Idea Source (Optional)"
                maxlength={5000}
                name="source"
                onFocus={handleFocus}
                onBlur={handleBlur}
                isActive={activeField === 'source'}
                hint={
                  <>
                    {/* eslint-disable-next-line max-len */}
                    If this is your original idea write your name or if you have found this on the internet, link the{' '}
                    <strong>source</strong> here. For example, if you found it in some Twitter thread, paste the link
                    here.
                  </>
                }
                error={{
                  'Please enter a valid URL': values.source.trim() && !isValidURL(values.source),
                }}
                onChange={handleChange}
              />
              <Button
                stretch
                type="submit"
                style={{ marginTop: '24px' }}
                disabled={isSubmitDisabled}
                loading={submitIdeaStatus === STATUS.REQUEST}
              >
                Submit Idea
              </Button>
            </form>
          </Card>
        </Container>
      </Page>
    </>
  );
};

export default SubmitIdea;
